import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../components/auth/AuthContext";
import { HeaderBar } from "../components/layout/TopBar";
import NavBar from "../components/layout/NavBar";
import { getEnrichmentBases, setEnrichmentBase } from "../api/adminApi";
import { Hero, ToggleSwitch, EmptyState } from "../components/adminSurface/AdminSurface";
import { LayersArt } from "../components/adminSurface/heroArt";

const Th = ({ children, style }) => (
  <th style={{ textAlign: "left", padding: "10px 14px", fontSize: 12, fontWeight: 600,
               color: "#6b7280", textTransform: "uppercase", letterSpacing: ".04em", ...style }}>
    {children}
  </th>
);

const Td = ({ children, style }) => (
  <td style={{ padding: "12px 14px", fontSize: 14, color: "#111827", ...style }}>{children}</td>
);

/**
 * Turning document enrichment on, one base at a time.
 *
 * There is deliberately no "enable all" control on this page and there will not be one. Enrichment
 * writes a sidecar row per document: affordable for a DMS-shaped base of thousands to millions, and
 * ruinous for a print archive of hundreds of millions. Every base is an individual decision by
 * somebody who knows which kind they are looking at, which is why the switches sit one per row rather
 * than behind a bulk action.
 */
export default function EnrichmentPage() {
  const { t } = useTranslation();
  const { token } = useAuth();

  const [bases, setBases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(null);
  // Messages are held as a key (plus params) or as text the server sent, and translated at RENDER
  // time. Translating inside a callback would put `t` in its dependency list, and `t` is a fresh
  // function on every render - which turns the load effect into an endless loop.
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [search, setSearch] = useState("");

  const say = (m) => (m == null ? "" : m.text != null ? m.text : t(m.key, m.params));

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await getEnrichmentBases(token);
      setBases(Array.isArray(rows) ? rows : []);
    } catch (e) {
      // A licence without ai-retrieval answers 403 here, which is a normal state for most
      // installations rather than a fault - so it is explained, not dressed as an error.
      setBases([]);
      setError(
        e?.status === 403
          ? { key: "enrichment.notLicensed" }
          : e?.message ? { text: e.message } : { key: "enrichment.loadFailed" }
      );
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { refresh(); }, [refresh]);

  const toggle = async (row, next) => {
    setSaving(row.id);
    setError(null);
    setInfo(null);
    try {
      const res = await setEnrichmentBase(token, row.id, next);
      setBases((list) => list.map((b) => (b.id === row.id ? { ...b, enrichmentEnabled: next } : b)));
      setInfo(res?.note
        ? { text: res.note }
        : { key: next ? "enrichment.turnedOn" : "enrichment.turnedOff", params: { db: row.dbName } });
    } catch (e) {
      setError(e?.message ? { text: e.message } : { key: "enrichment.saveFailed" });
    } finally {
      setSaving(null);
    }
  };

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return bases;
    return bases.filter((b) =>
      String(b.dbName || "").toLowerCase().includes(q) ||
      String(b.organization || "").toLowerCase().includes(q));
  }, [bases, search]);

  const enabledCount = bases.filter((b) => b.enrichmentEnabled).length;

  return (
    <div className="app-root">
      <div className="nav-strip"><NavBar /></div>
      <HeaderBar />

      <div className="page-container" style={{ paddingBottom: 40 }}>
        <Hero
          icon="🧭"
          title={t("enrichment.title")}
          subtitle={t("enrichment.subtitle")}
          statValue={loading ? "…" : `${enabledCount}/${bases.length}`}
          statLabel={t("enrichment.statEnabled")}
          onRefresh={refresh}
          refreshing={loading}
          art={<LayersArt />}
        />

        {info ? (
          <div className="asf-alert" style={{ color: "#15803d", background: "#dcfce7", borderColor: "#bbf7d0" }}>
            ✓ {say(info)}
          </div>
        ) : null}
        {error ? <div className="asf-alert">{say(error)}</div> : null}

        <div className="asf-card">
          {/* Said once, at the top, because it is the thing an administrator most needs to know
              before flipping anything: this is per base, and the reason it is per base. */}
          <p style={{ margin: "0 0 14px", fontSize: 13, color: "#6b7280", lineHeight: 1.6 }}>
            {t("enrichment.intro")}
          </p>

          <div className="asf-grid" style={{ alignItems: "flex-end" }}>
            <div className="asf-field" style={{ flex: "1 1 260px" }}>
              <label className="asf-lbl">{t("common.search")}</label>
              <input
                className="asf-input"
                type="text"
                placeholder={t("enrichment.searchPlaceholder")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </div>

        {shown.length === 0 && !loading ? (
          <EmptyState
            icon="🗄"
            title={t("enrichment.emptyTitle")}
            hint={search ? t("enrichment.emptySearchHint") : t("enrichment.emptyHint")}
          />
        ) : (
          <div className="asf-tablecard">
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #eef0f4" }}>
                  <Th>{t("enrichment.colBase")}</Th>
                  <Th>{t("enrichment.colOrganization")}</Th>
                  <Th style={{ width: 160 }}>{t("enrichment.colState")}</Th>
                  <Th style={{ width: 120, textAlign: "right" }}>{t("enrichment.colEnabled")}</Th>
                </tr>
              </thead>
              <tbody>
                {shown.map((b) => (
                  <tr key={b.id} className="asf-row">
                    <Td>
                      <span style={{ fontWeight: 600 }}>{b.dbName}</span>
                      {b.isClosed ? (
                        <span className="asf-chip" style={{ marginLeft: 8 }}>{t("enrichment.closed")}</span>
                      ) : null}
                    </Td>
                    <Td style={{ color: "#6b7280" }}>{b.organization || "—"}</Td>
                    <Td>
                      <span className={`asf-status ${b.enrichmentEnabled ? "asf-status--on" : "asf-status--off"}`}>
                        {b.enrichmentEnabled ? t("enrichment.stateOn") : t("enrichment.stateOff")}
                      </span>
                    </Td>
                    <Td style={{ textAlign: "right" }}>
                      <ToggleSwitch
                        checked={!!b.enrichmentEnabled}
                        onChange={(next) => saving === null && toggle(b, next)}
                      />
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* What turning it off does NOT do. Worth stating on the page rather than only in the
            response, because the fear of losing corrections is what stops people trying it. */}
        <p style={{ margin: "16px 2px 0", fontSize: 12, color: "#9ca3af", lineHeight: 1.6 }}>
          {t("enrichment.offNote")}
        </p>
      </div>
    </div>
  );
}
